import cn from 'classnames';

import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import Heading from 'components/heading/Heading';
import HtmlString from 'components/html-string/HtmlString';
import LinkWrapper from 'components/link-wrapper/LinkWrapper';
import Metadata from 'components/metadata/Metadata';
import ListItemImage from 'components/list-item-image/ListItemImage';
import DateCardStatus from 'components/date-card/DateCardStatus';
import DateCardTags from 'components/date-card/DateCardTags';
import DocumentIcon from 'components/document-icon/DocumentIcon';

import { ISearchResult } from 'source/NFR.types';

export const searchResultThemes = defineTheme({
  darkText: 'search-result--theme-dark-text',
  sidebar: 'search-result--theme-sidebar',
  noBorder: 'search-result--theme-no-border'
});
type Theme = ThemeKeys<typeof searchResultThemes>;

interface ISearchResultProps extends ISearchResult {
  headingLevel?: number;
  theme?: Theme;
  isLink?: boolean;
}

const SearchResult: React.FC<ISearchResultProps> = ({
  headingLevel = 2,
  text,
  title,
  theme,
  metadata,
  url,
  trackUrl,
  image,
  themeTags,
  statusList,
  icon,
  isLink,
  descriptiveTitle
}) => {
  const { OuterLink, InnerLink } = LinkWrapper(!!isLink, url, trackUrl);
  return (
    <OuterLink className={cn('search-result', searchResultThemes(theme))}>
      {descriptiveTitle && <HtmlString className="search-result__descriptive-title" text={descriptiveTitle} />}
      <InnerLink className="search-result--link">
        <Heading
          level={headingLevel}
          className={cn(
            'search-result__title',
            { 'search-result__title--has-descriptive-title': descriptiveTitle },
            searchResultThemes(theme)
          )}
        >
          <HtmlString text={title} />
        </Heading>
      </InnerLink>
      {statusList && (
        <div className="search-result__status">
          {statusList?.map(status => (
            <DateCardStatus key={status.text} {...status} />
          ))}
        </div>
      )}
      {metadata && (
        <div className="search-result__metadata">
          <Metadata theme={theme === 'sidebar' ? 'vertical' : undefined} {...metadata} />
        </div>
      )}
      <div className="search-result__content">
        {image && (
          <div className="search-result__image-container">
            <ListItemImage image={image} />
          </div>
        )}
        {icon && !image && (
          <div className="search-result__icon">
            <DocumentIcon iconTheme={icon.iconTheme} size="xsmall" isBlack />
          </div>
        )}
        {text && <HtmlString text={text} />}
      </div>
      {themeTags && <DateCardTags {...themeTags} />}
    </OuterLink>
  );
};

SearchResult.displayName = 'SearchResult';

export default SearchResult;
